import { Box } from '@chakra-ui/react'

import { TailgateGridQuery } from '@/graphql/generated/operations'

const DEFAULT_VIEW_WIDTH = 1920
const DEFAULT_VIEW_HEIGHT = 1080

interface ScalableGridOverlayIProps {
  tailgateGrid: TailgateGridQuery['tailgateGrid']
  viewWidth?: number | null
  viewHeight?: number | null
}

const getBoxPoints = (
  box: NonNullable<TailgateGridQuery['tailgateGrid']>['inBox']
): string => {
  if (!box) return ''
  const { topLeft, topRight, bottomRight, bottomLeft } = box
  return [
    `${topLeft.x},${topLeft.y}`,
    `${topRight.x},${topRight.y}`,
    `${bottomRight.x},${bottomRight.y}`,
    `${bottomLeft.x},${bottomLeft.y}`,
  ].join(' ')
}

const getCentroid = (
  box: NonNullable<TailgateGridQuery['tailgateGrid']>['inBox']
): { x: number; y: number } => {
  if (!box) return { x: 0, y: 0 }
  const { topLeft, topRight, bottomRight, bottomLeft } = box
  return {
    x: (topLeft.x + topRight.x + bottomRight.x + bottomLeft.x) / 4,
    y: (topLeft.y + topRight.y + bottomRight.y + bottomLeft.y) / 4,
  }
}

/**
 * Renders a tailgate grid overlay on top of a responsive video container.
 *
 * Uses SVG viewBox so the grid scales correctly regardless of the container's
 * display dimensions. `preserveAspectRatio="none"` is intentional: the video
 * player and camera share the same 16:9 aspect ratio, so no distortion occurs.
 *
 * Designed to be placed as an absolutely-positioned sibling to VideoPlayer,
 * inside a `position="relative"` wrapper Box.
 */
export const ScalableGridOverlay = ({
  tailgateGrid,
  viewWidth,
  viewHeight,
}: ScalableGridOverlayIProps) => {
  if (!tailgateGrid) return null

  const width = viewWidth ?? DEFAULT_VIEW_WIDTH
  const height = viewHeight ?? DEFAULT_VIEW_HEIGHT
  const { inBox, outBox } = tailgateGrid

  const inCentroid = getCentroid(inBox)
  const outCentroid = getCentroid(outBox)

  return (
    <Box
      h='100%'
      left={0}
      pointerEvents='none'
      position='absolute'
      top={0}
      w='100%'
      zIndex={1}
    >
      <svg
        height='100%'
        preserveAspectRatio='none'
        style={{ position: 'absolute', top: 0, left: 0 }}
        viewBox={`0 0 ${width} ${height}`}
        width='100%'
      >
        {inBox && (
          <>
            <polygon
              fill='rgba(72, 187, 120, 0.25)'
              points={getBoxPoints(inBox)}
              stroke='rgba(72, 187, 120, 0.8)'
              strokeWidth='4'
              style={{ vectorEffect: 'non-scaling-stroke' }}
            />
            <text
              fill='rgba(72, 187, 120, 0.9)'
              fontSize={Math.round(height * 0.04)}
              fontWeight='bold'
              textAnchor='middle'
              x={inCentroid.x}
              y={inCentroid.y}
            >
              IN
            </text>
          </>
        )}
        {outBox && (
          <>
            <polygon
              fill='rgba(245, 101, 101, 0.25)'
              points={getBoxPoints(outBox)}
              stroke='rgba(245, 101, 101, 0.8)'
              strokeWidth='4'
              style={{ vectorEffect: 'non-scaling-stroke' }}
            />
            <text
              fill='rgba(245, 101, 101, 0.9)'
              fontSize={Math.round(height * 0.04)}
              fontWeight='bold'
              textAnchor='middle'
              x={outCentroid.x}
              y={outCentroid.y}
            >
              OUT
            </text>
          </>
        )}
      </svg>
    </Box>
  )
}
